import React, { FC, useEffect, useRef } from 'react'
import { Modal, Button, Card } from 'react-bootstrap'
import useLogoutUser from '../../../hooks/authentication/useLogoutUser'
import useUserAuthenticated from '../../../hooks/authentication/useUserAuthenticated'
import useUserWithRole from '../../../hooks/authentication/useUserWithRole'
import { useAuthState } from '../../../hooks/store/useAuthState'

const SessionTimeoutModal: FC = () => {
    const { refetch: refetchAuth } = useUserAuthenticated()
    const { user, removeUser } = useUserWithRole()
    const logoutUser = useLogoutUser()
    const {
        showTimeoutWarning,
        setShowTimeoutWarning,
        setSessionTimeOut
    } = useAuthState()

    const timeoutRef = useRef<NodeJS.Timeout | null>(null)
    const extendButtonRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        if (logoutUser.isSuccess) {
            removeUser()
            setShowTimeoutWarning(false)
        }
    }, [logoutUser.isSuccess])

    useEffect(() => {
        const checkSessionAndStartTimer = async () => {
            const { data, isError } = await refetchAuth()

            if (isError || !data?.expireTime || new Date(data.expireTime) <= new Date()) {
                handleLogoutUser()
                return
            }

            timeoutRef.current = setTimeout(() => {
                handleLogoutUser()
                setSessionTimeOut(true)
            }, 20000)

            extendButtonRef.current?.focus()
        }

        if (showTimeoutWarning) {
            checkSessionAndStartTimer()
        }

        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current)
        }
    }, [showTimeoutWarning])


    const handleLogoutUser = () => {
        setShowTimeoutWarning(false)
        logoutUser.mutate({ refreshToken: user.refresh_token })
    }

    const extendSession = async () => {
        try {
            const { data, isError } = await refetchAuth()
            const expireTime = data?.expireTime

            if (!isError && expireTime && new Date(expireTime) > new Date()) {
                sessionStorage.setItem('sessionTimeOut', new Date(expireTime).toISOString())
                setShowTimeoutWarning(false)
            } else {
                handleLogoutUser()
            }
        } catch {
            handleLogoutUser()
        }
    }

    return (
        <Modal
            show={showTimeoutWarning}
            size="lg"
            aria-labelledby="session-timeout-modal-title"
            backdrop="static"
            keyboard={false}
            aria-live="assertive"
        >
            <Modal.Header>
                <Modal.Title id="session-timeout-modal-title">
                    Session Expiring
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Card.Text>
                    Your session is about to expire. Do you want to extend it?
                </Card.Text>
                <div className="d-flex justify-content-end gap-2 mt-3">
                    <Button
                        variant="secondary"
                        onClick={handleLogoutUser}
                    >
                        Log Out
                    </Button>
                    <Button
                        ref={extendButtonRef}
                        variant="primary"
                        onClick={extendSession}
                        aria-label="Extend Session"
                    >
                        Extend Session
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    )
}

export default SessionTimeoutModal
